import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo } from 'react';
import { skipToken } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { usePapaParse } from 'react-papaparse';
import { useForm, Controller } from 'react-hook-form';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import { Select } from 'chakra-react-select';

import { djs } from '@duke-hq/djs';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import type { RouterInputs, RouterOutputs } from '@duke-hq/svc-api/admin';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { toRtoRange } from './toRtoRange';
import { downloadCsv } from '../../lib/download';

type ReportInput = RouterInputs['providers']['downloadEmailReport'];
type Role = ReportInput['roles'][number];
type Grouping = ReportInput['groupBy'];
type ReportRow = RouterOutputs['providers']['downloadEmailReport'][number];

const roleLabels: Record<Role, () => string> = {
  CONSULTANT: () => t`Consultant`,
  SITE_MANAGER: () => t`Site manager`,
  REGIONAL_MANAGER: () => t`Regional manager`,
  STATE_MANAGER: () => t`State manager`,
  NATIONAL_MANAGER: () => t`National manager`,
};

const groupingLabels: Record<Grouping, () => string> = {
  recipient: () => t`Recipient`,
  template: () => t`Template`,
  provider: () => t`Provider`,
  month: () => t`Month`,
};

function isGrouping(value: string): value is Grouping {
  return value in groupingLabels;
}

function isRole(value: string): value is Role {
  return value in roleLabels;
}

type Option<T> = { value: T; label: string };

type FormData = {
  provider: Option<number> | null;
  roles: Option<Role>[];
  groupBy: Grouping;
  templates: Option<string>[];
  dateRange: Date[];
};

function monthsAgo(months: number) {
  return [djs().subtract(months, 'month').startOf('day').toDate(), djs().startOf('day').toDate()];
}

function roleLabel(role: string) {
  return isRole(role) ? roleLabels[role]() : role;
}

function formatRate(ratePct: number | null) {
  return ratePct == null ? '' : ratePct.toFixed(1);
}

function groupColumns(row: ReportRow, groupBy: Grouping) {
  switch (groupBy) {
    case 'recipient':
      return {
        name: row.label,
        email: row.recipient?.email ?? '',
        role: row.recipient == null ? '' : roleLabel(row.recipient.role),
        status: row.recipient?.status ?? '',
        last_active: row.recipient?.lastActive ?? '',
      };
    case 'template':
      return { template: row.label };
    case 'provider':
      return {};
    case 'month':
      return { month: row.label };
  }
}

function toCsvRow(row: ReportRow, groupBy: Grouping, includeProvider: boolean) {
  return {
    ...(includeProvider ? { provider: row.provider } : {}),
    ...groupColumns(row, groupBy),
    emails_sent: row.emailsSent,
    emails_delivered: row.emailsDelivered,
    delivery_rate_pct: formatRate(row.deliveryRatePct),
    emails_bounced: row.emailsBounced,
    bounce_rate_pct: formatRate(row.bounceRatePct),
    emails_complained: row.emailsComplained,
    complaint_rate_pct: formatRate(row.complaintRatePct),
    emails_opened: row.emailsOpened,
    open_rate_pct: formatRate(row.openRatePct),
    emails_clicked: row.emailsClicked,
    click_rate_pct: formatRate(row.clickRatePct),
  };
}

export function EmailReport() {
  const toast = useToast();
  const { jsonToCSV } = usePapaParse();

  const roleOptions = useMemo(
    () =>
      Object.keys(roleLabels).flatMap((value) =>
        isRole(value) ? [{ value, label: roleLabels[value]() }] : [],
      ),
    [],
  );

  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      provider: null,
      roles: [{ value: 'CONSULTANT', label: roleLabels.CONSULTANT() }],
      groupBy: 'recipient',
      templates: [],
      dateRange: monthsAgo(12),
    },
    mode: 'onChange',
  });

  const selectedProvider = watch('provider');
  const templatesRange = toRtoRange(watch('dateRange'));
  const selectedRoles = watch('roles');

  const { data: tenants } = trpcAdmin.providers.all.useQuery(undefined, { initialData: [] });
  const { data: templateNames } = trpcAdmin.providers.emailReportTemplates.useQuery(
    templatesRange != null && selectedRoles.length > 0
      ? {
          providerId: selectedProvider?.value,
          roles: selectedRoles.map((role) => role.value),
          ...templatesRange,
        }
      : skipToken,
    { placeholderData: (previous) => previous },
  );

  const providerOptions = useMemo(
    () =>
      tenants
        .map((tenant) => ({ value: tenant.id, label: `${tenant.name} (${tenant.subdomain})` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [tenants],
  );

  const templateOptions = useMemo(
    () => (templateNames ?? []).map((name) => ({ value: name, label: name })),
    [templateNames],
  );

  useEffect(() => {
    if (templateNames == null) {
      return;
    }
    const available = new Set(templateNames);
    const selected = getValues('templates');
    const stillAvailable = selected.filter((template) => available.has(template.value));
    if (stillAvailable.length !== selected.length) {
      setValue('templates', stillAvailable);
    }
  }, [templateNames, getValues, setValue]);

  const { mutate: downloadEmailReport, isPending: isDownloading } =
    trpcAdmin.providers.downloadEmailReport.useMutation({
      onError: (error) => {
        toast({
          title: t`Failed to generate report`,
          description: error.message,
          status: 'error',
          duration: 5000,
        });
      },
    });

  const saveReport = useCallback(
    (rows: ReportRow[], data: FormData) => {
      if (rows.length === 0) {
        toast({
          title: t`No emails match the selected filters`,
          status: 'info',
          duration: 5000,
        });
        return;
      }

      const includeProvider = data.provider == null;
      const csvData = jsonToCSV(
        rows.map((row) => toCsvRow(row, data.groupBy, includeProvider)),
        { escapeFormulae: true },
      );
      const providerName = (data.provider?.label ?? 'all_providers').replace(/[^a-zA-Z0-9]/g, '_');
      downloadCsv(
        csvData,
        `${providerName}-email-report-by-${data.groupBy}_${data.dateRange.map((date) => djs(date).format('YYYY-MM-DD')).join('_')}.csv`,
      );

      toast({
        title: t`Report generated successfully`,
        description: t`Your CSV report is being downloaded`,
        status: 'success',
        duration: 5000,
      });
    },
    [jsonToCSV, toast],
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      const range = toRtoRange(data.dateRange);
      if (range == null) {
        return;
      }

      downloadEmailReport(
        {
          providerId: data.provider?.value,
          roles: data.roles.map((role) => role.value),
          groupBy: data.groupBy,
          templates: data.templates.map((template) => template.value),
          ...range,
        },
        { onSuccess: (rows) => saveReport(rows, data) },
      );
    },
    [downloadEmailReport, saveReport],
  );

  return (
    <VStack alignItems="start" w="full" p={4}>
      <Text textStyle="large" fontWeight="medium">
        <Trans>Provider staff email report</Trans>
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap={8} h="full">
          <VStack alignItems="flex-start" spacing={4}>
            <Box minW={pxToRem(320)}>
              <Controller
                name="provider"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1}>
                      <Trans>Provider</Trans>
                    </FormLabel>
                    <Select
                      isSearchable
                      isClearable
                      placeholder={t`All providers`}
                      options={providerOptions}
                      value={field.value}
                      onChange={(option) => {
                        field.onChange(option);
                        if (option != null && watch('groupBy') === 'provider') {
                          setValue('groupBy', 'recipient');
                        }
                      }}
                    />
                  </FormControl>
                )}
              />
            </Box>

            <Box minW={pxToRem(320)}>
              <Controller
                name="roles"
                control={control}
                rules={{
                  validate: (value) => value.length > 0 || t`Please select at least one role`,
                }}
                render={({ field }) => (
                  <FormControl isInvalid={!!errors.roles} isRequired>
                    <FormLabel fontSize="sm" mb={1}>
                      <Trans>Recipient roles</Trans>
                    </FormLabel>
                    <Select
                      isMulti
                      placeholder={t`Select roles...`}
                      options={roleOptions}
                      value={field.value}
                      onChange={field.onChange}
                      closeMenuOnSelect={false}
                    />
                    <FormErrorMessage>{errors.roles?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />
            </Box>

            <Controller
              name="groupBy"
              control={control}
              render={({ field }) => (
                <FormControl as="fieldset">
                  <FormLabel as="legend" fontSize="sm" mb={1}>
                    <Trans>Group by</Trans>
                  </FormLabel>
                  <RadioGroup
                    value={field.value}
                    onChange={(value) => {
                      if (isGrouping(value)) {
                        field.onChange(value);
                      }
                    }}
                  >
                    <HStack spacing={4}>
                      {Object.entries(groupingLabels).map(([value, label]) => (
                        <Radio
                          key={value}
                          value={value}
                          isDisabled={value === 'provider' && selectedProvider != null}
                        >
                          {label()}
                        </Radio>
                      ))}
                    </HStack>
                  </RadioGroup>
                </FormControl>
              )}
            />

            <Box minW={pxToRem(320)}>
              <Controller
                name="templates"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1}>
                      <Trans>Templates</Trans>
                    </FormLabel>
                    <Select
                      isMulti
                      isSearchable
                      placeholder={t`All templates`}
                      options={templateOptions}
                      value={field.value}
                      onChange={field.onChange}
                      closeMenuOnSelect={false}
                    />
                  </FormControl>
                )}
              />
            </Box>

            <Controller
              name="dateRange"
              control={control}
              rules={{
                validate: (value) =>
                  (value.length === 2 && value[0] != null && value[1] != null) ||
                  t`Please select both start and end dates`,
              }}
              render={({ field }) => (
                <FormControl isInvalid={!!errors.dateRange} isRequired>
                  <FormLabel fontSize="sm" mb={1}>
                    <Trans>Emails sent between</Trans>
                  </FormLabel>
                  <ButtonGroup isAttached variant="outline" maxW="fit-content">
                    <RangeDatepicker
                      selectedDates={field.value}
                      onDateChange={field.onChange}
                      maxDate={new Date()}
                      configs={{ dateFormat: 'dd/MM/yyyy' }}
                    />
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label={t`Date range select options`}
                        icon={<Icon as={ChevronDown} boxSize={4} />}
                        borderLeftRadius={0}
                      />
                      <MenuList>
                        <MenuItem
                          onClick={() =>
                            setValue('dateRange', monthsAgo(3), { shouldValidate: true })
                          }
                        >
                          <Trans>Last 3 months</Trans>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            setValue('dateRange', monthsAgo(6), { shouldValidate: true })
                          }
                        >
                          <Trans>Last 6 months</Trans>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            setValue('dateRange', monthsAgo(12), { shouldValidate: true })
                          }
                        >
                          <Trans>Last 12 months</Trans>
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </ButtonGroup>
                  <FormHelperText>
                    <Trans>
                      Delivery and open tracking starts on 13 February 2026, so rates for emails
                      sent before then are not meaningful. Some providers' mail scanners open every
                      email on arrival, so an open rate near 100% is not a reliable measure of
                      engagement.
                    </Trans>
                  </FormHelperText>
                  <FormErrorMessage>{errors.dateRange?.message}</FormErrorMessage>
                </FormControl>
              )}
            />
          </VStack>
          <Button type="submit" variant="primary" isLoading={isDownloading} width="fit-content">
            <Trans>Download CSV</Trans>
          </Button>
        </Flex>
      </form>
    </VStack>
  );
}
